import { useEffect, useState } from "react";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ConsentChoices,
  getStoredConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  saveConsent,
} from "@/lib/consent";

/**
 * GDPR / §25 TTDSG cookie consent modal.
 *
 * Compliance shape:
 * - Non-blocking bottom banner (slides up on first visit): the site stays
 *   browsable, but no non-essential service runs until a choice is saved
 *   (see src/lib/consent.ts + main.tsx).
 * - "Accept all" and "Only essential" sit side by side on the first layer
 *   (rejecting is as easy as accepting — no dark pattern).
 * - Granular per-category toggles behind "Customize"; nothing pre-ticked.
 * - Hidden on the legal pages so visitors can read the privacy policy and
 *   imprint before deciding (informed consent, and those pages stay usable).
 * - Withdrawal: openCookieSettings() (src/lib/consent.ts) is linked from the
 *   footer ("Cookie settings"), Art. 7(3) — withdrawing as easy as consenting.
 */

/** Consent must never block reading the documents it refers to. */
const LEGAL_ROUTES = new Set([
  "/privacy-policy",
  "/imprint",
  "/terms-of-service",
]);

type CopyKey = keyof (typeof STRINGS)["en"];

const STRINGS = {
  en: {
    title: "Privacy settings",
    intro:
      "We use cookies and similar technologies. Essential ones keep the site working (login, security, language). With your consent we also use analytics to understand how the site is used, and error diagnostics to fix problems. You can change or withdraw your choice at any time via “Cookie settings” in the footer.",
    privacyPolicy: "Privacy Policy",
    imprint: "Imprint",
    acceptAll: "Accept all",
    essentialOnly: "Only essential",
    customize: "Customize",
    save: "Save selection",
    back: "Back",
    close: "Close",
    alwaysActive: "Always active",
    essentialTitle: "Essential",
    essentialDesc:
      "Login, security, your language preference and saving this consent choice. Required for the site to work.",
    analyticsTitle: "Analytics (PostHog)",
    analyticsDesc:
      "Helps us understand how the site is used (pages visited, clicks). Processed on PostHog's EU servers.",
    errorTrackingTitle: "Error diagnostics (Sentry)",
    errorTrackingDesc:
      "Sends a report (including your IP address) when something breaks, so we can find and fix the problem.",
  },
  de: {
    title: "Datenschutz-Einstellungen",
    intro:
      "Wir verwenden Cookies und ähnliche Technologien. Essenzielle sorgen dafür, dass die Website funktioniert (Login, Sicherheit, Sprache). Mit Ihrer Einwilligung nutzen wir außerdem Analyse, um die Nutzung der Website zu verstehen, und Fehlerdiagnose, um Probleme zu beheben. Sie können Ihre Auswahl jederzeit über „Cookie-Einstellungen“ im Footer ändern oder widerrufen.",
    privacyPolicy: "Datenschutzerklärung",
    imprint: "Impressum",
    acceptAll: "Alle akzeptieren",
    essentialOnly: "Nur essenzielle",
    customize: "Anpassen",
    save: "Auswahl speichern",
    back: "Zurück",
    close: "Schließen",
    alwaysActive: "Immer aktiv",
    essentialTitle: "Essenziell",
    essentialDesc:
      "Login, Sicherheit, Ihre Spracheinstellung und das Speichern dieser Auswahl. Für den Betrieb der Website erforderlich.",
    analyticsTitle: "Analyse (PostHog)",
    analyticsDesc:
      "Hilft uns zu verstehen, wie die Website genutzt wird (besuchte Seiten, Klicks). Verarbeitung auf EU-Servern von PostHog.",
    errorTrackingTitle: "Fehlerdiagnose (Sentry)",
    errorTrackingDesc:
      "Sendet einen Bericht (inklusive Ihrer IP-Adresse), wenn etwas nicht funktioniert, damit wir das Problem beheben können.",
  },
} as const;

function CategoryRow({
  title,
  description,
  checked,
  onCheckedChange,
  lockedLabel,
}: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange?: (value: boolean) => void;
  /** Set for the essential category: renders a static "always active" tag. */
  lockedLabel?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-black/[0.07] bg-[#faf8f3] p-4">
      <div>
        <p className="text-sm font-semibold text-[#0f2e28]">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-[#0f2e28]/55">
          {description}
        </p>
      </div>
      {lockedLabel ? (
        <span className="shrink-0 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          {lockedLabel}
        </span>
      ) : (
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label={title}
          className="mt-0.5 shrink-0"
        />
      )}
    </div>
  );
}

export function CookieConsent() {
  const { t, language } = useLanguage();
  const location = useLocation();

  const [open, setOpen] = useState(() => getStoredConsent() === null);
  // True when reopened via the footer link (existing consent) — closable then.
  const [manualOpen, setManualOpen] = useState(false);
  const [view, setView] = useState<"summary" | "settings">("summary");
  const [analytics, setAnalytics] = useState(false);
  const [errorTracking, setErrorTracking] = useState(false);

  const copy = (key: CopyKey): string =>
    /*
      baseLanguageFor, NOT `language`. STRINGS only has en/de, and this is
      a dynamic index -- with the six-language context a French or Arabic
      visitor hits STRINGS["fr"], which is undefined, and `undefined[key]`
      THROWS before the ?? fallback is ever evaluated. CookieConsent is
      mounted globally, so that takes the whole app down on first visit.
      (Codex, 15 Oct.)
    */
    t(
      `cookieConsent.${key}`,
      STRINGS[baseLanguageFor(language)][key] ?? STRINGS.en[key]
    );

  useEffect(() => {
    const onOpenSettings = () => {
      const stored = getStoredConsent();
      setAnalytics(stored?.analytics ?? false);
      setErrorTracking(stored?.errorTracking ?? false);
      setView("settings");
      setManualOpen(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpenSettings);
    return () =>
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpenSettings);
  }, []);

  const visible = open && (manualOpen || !LEGAL_ROUTES.has(location.pathname));

  if (!visible) return null;

  const decide = (choices: ConsentChoices) => {
    saveConsent(choices);
    setOpen(false);
    setManualOpen(false);
    setView("summary");
  };

  const dismissWithoutSaving = () => {
    // Only offered when a valid consent record already exists.
    setOpen(false);
    setManualOpen(false);
    setView("summary");
  };

  return (
    // Non-modal bottom banner: no backdrop and no scroll lock, so the site
    // stays fully browsable while the choice is pending (nothing tracks
    // until it's made). pointer-events split keeps the page clickable
    // around the card.
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[90] flex justify-center px-3 pb-3 sm:px-4 sm:pb-6"
      role="dialog"
      aria-labelledby="cookie-consent-title"
    >
      <div className="pointer-events-auto max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-[1.75rem] border border-black/[0.07] bg-white p-6 shadow-[0_40px_90px_-30px_rgba(15,46,40,0.55)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-8 motion-safe:duration-500 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10">
              <Cookie className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <h2
              id="cookie-consent-title"
              className="font-display text-xl font-semibold text-[#0f2e28]"
            >
              {copy("title")}
            </h2>
          </div>
          {manualOpen && (
            <button
              type="button"
              onClick={dismissWithoutSaving}
              aria-label={copy("close")}
              className="rounded-full p-1.5 text-[#0f2e28]/40 transition-colors hover:bg-black/5 hover:text-[#0f2e28]"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[#0f2e28]/60">
          {copy("intro")}
        </p>
        <p className="mt-3 text-sm">
          <Link
            to="/privacy-policy"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {copy("privacyPolicy")}
          </Link>
          <span className="mx-2 text-[#0f2e28]/40">·</span>
          <Link
            to="/imprint"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {copy("imprint")}
          </Link>
        </p>

        {view === "settings" && (
          <div className="mt-4 space-y-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-300">
            <CategoryRow
              title={copy("essentialTitle")}
              description={copy("essentialDesc")}
              checked
              lockedLabel={copy("alwaysActive")}
            />
            <CategoryRow
              title={copy("analyticsTitle")}
              description={copy("analyticsDesc")}
              checked={analytics}
              onCheckedChange={setAnalytics}
            />
            <CategoryRow
              title={copy("errorTrackingTitle")}
              description={copy("errorTrackingDesc")}
              checked={errorTracking}
              onCheckedChange={setErrorTracking}
            />
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2.5 sm:flex-row">
          {view === "summary" ? (
            <>
              <button
                type="button"
                autoFocus
                onClick={() =>
                  decide({ analytics: false, errorTracking: false })
                }
                className="flex-1 rounded-full border border-black/10 bg-[#faf8f3] px-6 py-3 text-sm font-semibold text-[#0f2e28] transition-colors hover:bg-black/[0.04] active:scale-[0.99]"
              >
                {copy("essentialOnly")}
              </button>
              <button
                type="button"
                onClick={() => decide({ analytics: true, errorTracking: true })}
                className="flex-1 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[0_10px_28px_-10px_hsl(173,65%,45%,0.6)] transition-all hover:bg-primary-hover active:scale-[0.99]"
              >
                {copy("acceptAll")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => decide({ analytics, errorTracking })}
                className="flex-1 rounded-full border border-black/10 bg-[#faf8f3] px-6 py-3 text-sm font-semibold text-[#0f2e28] transition-colors hover:bg-black/[0.04] active:scale-[0.99]"
              >
                {copy("save")}
              </button>
              <button
                type="button"
                onClick={() => decide({ analytics: true, errorTracking: true })}
                className="flex-1 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[0_10px_28px_-10px_hsl(173,65%,45%,0.6)] transition-all hover:bg-primary-hover active:scale-[0.99]"
              >
                {copy("acceptAll")}
              </button>
            </>
          )}
        </div>
        {view === "summary" && (
          <button
            type="button"
            onClick={() => setView("settings")}
            className="mt-4 text-sm font-medium text-[#0f2e28]/55 underline-offset-2 transition-colors hover:text-primary hover:underline"
          >
            {copy("customize")}
          </button>
        )}
      </div>
    </div>
  );
}
